import React, { useState, useEffect, useRef, useCallback } from "react";
import { getScoped, setScoped, removeScoped } from '@/lib/userScopedStorage';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { cn } from "@/lib/utils";
import { Check, ChevronRight, Target, ChevronUp, ChevronDown, Eye } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";

// Setup stages - includes calibration for gaze tracking
type SetupStage = "avatar" | "calibration" | "test" | "cursor";

// MediaPipe face landmarks
interface FaceLandmark {
  x: number;
  y: number;
  z: number;
}

// Calibration point positions (9-point grid) - with margins to avoid cutoff
// Using 8% and 92% to keep points visible (accounting for status bar and edges)
const CALIBRATION_POINTS = [
  { id: 0, x: 8, y: 8 },     // Top-left (1)
  { id: 1, x: 50, y: 8 },    // Top-center (2)
  { id: 2, x: 92, y: 8 },    // Top-right (3)
  { id: 3, x: 8, y: 50 },    // Middle-left (4)
  { id: 4, x: 50, y: 50 },   // Center (5)
  { id: 5, x: 92, y: 50 },   // Middle-right (6)
  { id: 6, x: 8, y: 88 },    // Bottom-left (7) - higher to avoid status bar
  { id: 7, x: 50, y: 88 },   // Bottom-center (8)
  { id: 8, x: 92, y: 88 },   // Bottom-right (9)
];

// Calibration order: corners first, then center, then edges
// 1->9->3->7->5(center)->2->8->4->6
const CALIBRATION_ORDER = [0, 8, 2, 6, 4, 1, 7, 3, 5];

export default function GazeTrackingSetup() {
  const navigate = useNavigate();
  const { t, dir } = useTranslation();
  const [stage, setStage] = useState<SetupStage>("avatar");
  const [isTracking, setIsTracking] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [faceDistance, setFaceDistance] = useState<"good" | "too_close" | "too_far">("good");
  const [faceLandmarks, setFaceLandmarks] = useState<FaceLandmark[] | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  
  // Check if existing calibration exists
  const [hasExistingCalibration, setHasExistingCalibration] = useState(false);
  
  // Calibration state
  const [currentCalibrationIndex, setCurrentCalibrationIndex] = useState(0);
  const [calibrationData, setCalibrationData] = useState<{ screenX: number; screenY: number; irisX: number; irisY: number }[]>([]);
  const [isCollectingCalibration, setIsCollectingCalibration] = useState(false);
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  
  // Refs for DOM and MediaPipe
  const videoRef = useRef<HTMLVideoElement>(null);
  const meshCanvasRef = useRef<HTMLCanvasElement>(null);
  const faceLandmarkerRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  const stageRef = useRef<SetupStage>(stage);
  const calibrationTimerRef = useRef<NodeJS.Timeout>();
  const calibrationSamplesRef = useRef<{ irisX: number; irisY: number }[]>([]);
  const calibrationDataRef = useRef<{ screenX: number; screenY: number; irisX: number; irisY: number }[]>([]);
  const isCollectingCalibrationRef = useRef(false);
  
  // Gaze tracking calibration refs
  const calibrationCoefficientsRef = useRef<{ x: number[]; y: number[] } | null>(null);
  const cursorRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const cursorElementRef = useRef<HTMLDivElement>(null);
  const lastUpdateRef = useRef(0);
  
  // Calibration constants
  const SMOOTHING = 0.2;
  const SAMPLES_PER_POINT = 30; // Number of samples to collect per calibration point
  
  // Scroll buttons functionality
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const startScrolling = useCallback((direction: 'up' | 'down') => {
    if (scrollIntervalRef.current) return;
    
    const scrollAmount = direction === 'up' ? -40 : 40;
    scrollIntervalRef.current = setInterval(() => {
      window.scrollBy({ top: scrollAmount, behavior: 'auto' });
    }, 50);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Keep stageRef in sync
  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);

  // Keep isCollectingCalibrationRef in sync
  useEffect(() => {
    isCollectingCalibrationRef.current = isCollectingCalibration;
  }, [isCollectingCalibration]);

  // Hide body/html scrollbar during fullscreen stages (calibration, test, cursor)
  useEffect(() => {
    const isFullscreenStage = stage === 'calibration' || stage === 'test' || stage === 'cursor';
    if (isFullscreenStage) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('scrollbar-hide');
      document.documentElement.classList.add('scrollbar-hide');
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.classList.remove('scrollbar-hide');
      document.documentElement.classList.remove('scrollbar-hide');
    };
  }, [stage]);

  // State for initialization status
  const [initError, setInitError] = useState<string | null>(null);

  // Check for existing calibration on mount
  useEffect(() => {
    const savedCalibration = getScoped("gazeTrackingCalibration");
    if (savedCalibration) {
      try {
        const parsed = JSON.parse(savedCalibration);
        if (parsed.coefficients && parsed.coefficients.x && parsed.coefficients.y) {
          setHasExistingCalibration(true);
          // Pre-load the coefficients for potential skip
          calibrationCoefficientsRef.current = parsed.coefficients;
          console.log('[GazeTracking] Found existing calibration data');
        }
      } catch (e) {
        console.log('[GazeTracking] Invalid saved calibration');
      }
    }
  }, []);

  // Initialize MediaPipe
  const initializeMediaPipe = useCallback(async () => {
    try {
      console.log('[GazeTracking] Loading MediaPipe...');
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        filesetResolver,
        getFaceLandmarkerOptions('GPU', false)
      );
      
      console.log('[GazeTracking] MediaPipe loaded successfully');
      return true;
    } catch (err) {
      console.log('[GazeTracking] GPU failed, trying CPU...', err);
      try {
        const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
        
        const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
        
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          filesetResolver,
          getFaceLandmarkerOptions('CPU', false)
        );
        
        console.log('[GazeTracking] MediaPipe loaded with CPU fallback');
        return true;
      } catch (err2) {
        console.error('[GazeTracking] MediaPipe failed:', err2);
        setInitError(t("faceModelError"));
        return false;
      }
    }
  }, [t]);

  // Setup webcam
  const setupWebcam = useCallback(async () => {
    try {
      console.log('[GazeTracking] Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        console.log('[GazeTracking] Camera started successfully');
        return true;
      }
      setInitError(t("videoError"));
      return false;
    } catch (err) {
      console.error('[GazeTracking] Camera error:', err);
      setInitError(t("cameraError"));
      return false;
    }
  }, [t]);

  // Calculate face distance based on eye distance
  const calculateFaceDistance = (landmarks: FaceLandmark[]): "good" | "too_close" | "too_far" => {
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const eyeDistance = Math.abs(rightEye.x - leftEye.x);
    
    if (eyeDistance > 0.25) return "too_close";
    if (eyeDistance < 0.12) return "too_far";
    return "good";
  };

  // Get iris center (average of left and right iris landmarks)
  const getIrisCenter = (landmarks: FaceLandmark[]) => {
    // MediaPipe iris landmarks: 468-472 (left iris), 473-477 (right iris)
    // Center of iris: 468 (left), 473 (right)
    const leftIris = landmarks[468];
    const rightIris = landmarks[473];
    
    if (!leftIris || !rightIris) return null;
    
    return {
      x: (leftIris.x + rightIris.x) / 2,
      y: (leftIris.y + rightIris.y) / 2
    };
  };

  // Calculate gaze position using calibration coefficients
  const calculateGazePosition = useCallback((irisX: number, irisY: number) => {
    const coef = calibrationCoefficientsRef.current;
    if (!coef) return null;
    
    // Linear mapping from iris position to screen coordinates
    const screenX = coef.x[0] + coef.x[1] * irisX;
    const screenY = coef.y[0] + coef.y[1] * irisY;
    
    return { x: screenX, y: screenY };
  }, []);

  // Compute calibration coefficients using least squares
  const computeCalibrationCoefficients = useCallback((data: { screenX: number; screenY: number; irisX: number; irisY: number }[]) => {
    if (data.length < 4) return null;
    
    // Simple linear regression for X and Y separately
    const n = data.length;
    
    // For X: screenX = a + b * irisX
    let sumIrisX = 0, sumScreenX = 0, sumIrisX2 = 0, sumIrisXScreenX = 0;
    // For Y: screenY = c + d * irisY
    let sumIrisY = 0, sumScreenY = 0, sumIrisY2 = 0, sumIrisYScreenY = 0;
    
    for (const point of data) {
      sumIrisX += point.irisX;
      sumScreenX += point.screenX;
      sumIrisX2 += point.irisX * point.irisX;
      sumIrisXScreenX += point.irisX * point.screenX;
      
      sumIrisY += point.irisY;
      sumScreenY += point.screenY;
      sumIrisY2 += point.irisY * point.irisY;
      sumIrisYScreenY += point.irisY * point.screenY;
    }
    
    // Calculate coefficients for X
    const denomX = n * sumIrisX2 - sumIrisX * sumIrisX;
    const bX = denomX !== 0 ? (n * sumIrisXScreenX - sumIrisX * sumScreenX) / denomX : 0;
    const aX = (sumScreenX - bX * sumIrisX) / n;
    
    // Calculate coefficients for Y
    const denomY = n * sumIrisY2 - sumIrisY * sumIrisY;
    const bY = denomY !== 0 ? (n * sumIrisYScreenY - sumIrisY * sumScreenY) / denomY : 0;
    const aY = (sumScreenY - bY * sumIrisY) / n;
    
    return {
      x: [aX, bX],
      y: [aY, bY]
    };
  }, []);

  // Core tracking loop
  const runTrackingLoop = useCallback(() => {
    if (!videoRef.current || !faceLandmarkerRef.current) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      return;
    }
    
    const video = videoRef.current;
    const currentStage = stageRef.current;
    
    if (video.readyState >= 2) {
      const results = faceLandmarkerRef.current.detectForVideo(video, performance.now());
      
      if (results.faceLandmarks && results.faceLandmarks.length > 0) {
        const landmarks = results.faceLandmarks[0];
        
        // Get iris position for calibration and tracking
        const irisCenter = getIrisCenter(landmarks);
        
        // During calibration - collect samples using ref to avoid stale closure
        if (currentStage === "calibration" && isCollectingCalibrationRef.current && irisCenter) {
          calibrationSamplesRef.current.push({ irisX: irisCenter.x, irisY: irisCenter.y });
        }
        
        // During test/cursor stages - use calibration to calculate gaze position
        if ((currentStage === "test" || currentStage === "cursor") && irisCenter) {
          // Debug: log calibration state
          if (!calibrationCoefficientsRef.current) {
            console.log('[GazeTracking] Test stage: No calibration coefficients available!');
          }
          
          if (calibrationCoefficientsRef.current) {
            const gazePos = calculateGazePosition(irisCenter.x, irisCenter.y);
            
            if (gazePos) {
              // Smooth cursor movement
              cursorRef.current.x += (gazePos.x - cursorRef.current.x) * SMOOTHING;
              cursorRef.current.y += (gazePos.y - cursorRef.current.y) * SMOOTHING;
              
              // Clamp to screen bounds
              cursorRef.current.x = Math.max(0, Math.min(window.innerWidth, cursorRef.current.x));
              cursorRef.current.y = Math.max(0, Math.min(window.innerHeight, cursorRef.current.y));
              
              // Update cursor DOM element directly
              if (cursorElementRef.current) {
                cursorElementRef.current.style.left = `${cursorRef.current.x}px`;
                cursorElementRef.current.style.top = `${cursorRef.current.y}px`;
              }
              
              // Update cursor position state for dwell detection
              setCursorPos({ x: cursorRef.current.x, y: cursorRef.current.y });
            }
          }
        }
        
        // Throttled React state updates for face mesh rendering (every 100ms)
        const now = performance.now();
        if (now - lastUpdateRef.current > 100) {
          lastUpdateRef.current = now;
          setFaceLandmarks(landmarks);
          setFaceDistance(calculateFaceDistance(landmarks));
        }
      } else {
        const now = performance.now();
        if (now - lastUpdateRef.current > 100) {
          lastUpdateRef.current = now;
          setFaceLandmarks(null);
        }
      }
    }
    
    animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
  }, [calculateGazePosition]);

  // Start tracking
  const startTracking = useCallback(async () => {
    const mpReady = await initializeMediaPipe();
    if (!mpReady) return;
    
    const webcamReady = await setupWebcam();
    if (!webcamReady) return;
    
    setIsTracking(true);
    runTrackingLoop();
  }, [initializeMediaPipe, setupWebcam, runTrackingLoop]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    
    setIsTracking(false);
  }, []);

  // Initialize on mount
  useEffect(() => {
    startTracking();
    return () => stopTracking();
  }, []);

  // Start collecting calibration samples for current point
  const startCalibrationPoint = useCallback(() => {
    calibrationSamplesRef.current = [];
    setIsCollectingCalibration(true);
    isCollectingCalibrationRef.current = true;
    setCalibrationProgress(0);
    console.log('[GazeTracking] Started collecting for point', currentCalibrationIndex + 1);
    
    let sampleCount = 0;
    calibrationTimerRef.current = setInterval(() => {
      sampleCount++;
      setCalibrationProgress((sampleCount / SAMPLES_PER_POINT) * 100);
      
      if (sampleCount >= SAMPLES_PER_POINT) {
        clearInterval(calibrationTimerRef.current);
        setIsCollectingCalibration(false);
        isCollectingCalibrationRef.current = false;
        
        // Calculate average iris position for this point
        const samples = calibrationSamplesRef.current;
        console.log('[GazeTracking] Samples collected for point', currentCalibrationIndex + 1, ':', samples.length);
        
        if (samples.length > 0) {
          const avgIrisX = samples.reduce((sum, s) => sum + s.irisX, 0) / samples.length;
          const avgIrisY = samples.reduce((sum, s) => sum + s.irisY, 0) / samples.length;
          
          const pointIndex = CALIBRATION_ORDER[currentCalibrationIndex];
          const point = CALIBRATION_POINTS[pointIndex];
          const screenX = (point.x / 100) * window.innerWidth;
          const screenY = (point.y / 100) * window.innerHeight;
          
          // Store in ref for immediate access
          const newPoint = { screenX, screenY, irisX: avgIrisX, irisY: avgIrisY };
          calibrationDataRef.current = [...calibrationDataRef.current, newPoint];
          
          // Also update state for UI
          setCalibrationData(prev => [...prev, newPoint]);
          
          console.log('[GazeTracking] Calibration point collected:', currentCalibrationIndex + 1, '/', CALIBRATION_ORDER.length, newPoint);
        } else {
          console.error('[GazeTracking] No samples collected for point', currentCalibrationIndex + 1, '- iris tracking may not be working');
        }
        
        // Move to next point or finish calibration
        if (currentCalibrationIndex < CALIBRATION_ORDER.length - 1) {
          setCurrentCalibrationIndex(prev => prev + 1);
        } else {
          // Calibration complete - compute coefficients using ref data
          setTimeout(() => {
            const allData = calibrationDataRef.current;
            console.log('[GazeTracking] Computing coefficients with data points:', allData.length);
            
            const coefficients = computeCalibrationCoefficients(allData);
            console.log('[GazeTracking] Computed coefficients:', coefficients);
            
            if (coefficients) {
              calibrationCoefficientsRef.current = coefficients;
              // Save to localStorage for global engine
              setScoped('gazeTrackingCalibration', JSON.stringify({
                coefficients,
                trackingMode: 'gaze_tracking',
                timestamp: Date.now()
              }));
              // Dispatch event to notify the engine about calibration update
              window.dispatchEvent(new Event('gazeCalibrationUpdated'));
              console.log('[GazeTracking] Calibration saved, moving to test stage');
            } else {
              console.error('[GazeTracking] Failed to compute coefficients!');
            }
            
            setStage("test");
          }, 500);
        }
      }
    }, 50);
  }, [currentCalibrationIndex, computeCalibrationCoefficients]);

  // Auto-start calibration when reaching calibration stage
  useEffect(() => {
    if (stage === "calibration" && !isCollectingCalibration && currentCalibrationIndex < CALIBRATION_ORDER.length) {
      const timer = setTimeout(() => {
        startCalibrationPoint();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [stage, currentCalibrationIndex, isCollectingCalibration, startCalibrationPoint]);

  // Dwell detection for test/cursor stages
  const currentTargetRef = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    if (stage !== "test" && stage !== "cursor") return;
    
    const elementsAtPoint = document.elementsFromPoint(cursorPos.x, cursorPos.y);
    const interactiveElement = elementsAtPoint.find(el => 
      el.tagName === 'BUTTON' || 
      el.tagName === 'A' || 
      el.getAttribute('role') === 'button' ||
      el.classList.contains('clickable')
    ) as HTMLElement | undefined;
    
    if (interactiveElement) {
      if (currentTargetRef.current !== interactiveElement) {
        if (dwellTimerRef.current) {
          clearInterval(dwellTimerRef.current);
          dwellTimerRef.current = undefined;
        }
        setDwellProgress(0);
        currentTargetRef.current = interactiveElement;
      }
      
      if (!dwellTimerRef.current) {
        dwellTimerRef.current = setInterval(() => {
          setDwellProgress(prev => {
            if (prev >= 100) {
              clearInterval(dwellTimerRef.current!);
              dwellTimerRef.current = undefined;
              if (currentTargetRef.current) {
                currentTargetRef.current.click();
              }
              if (stage === "test" && currentTargetRef.current?.id === "test-target") {
                setTestCompleted(true);
              }
              currentTargetRef.current = null;
              return 0;
            }
            return prev + 4;
          });
        }, 50);
      }
    } else {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
        dwellTimerRef.current = undefined;
      }
      setDwellProgress(0);
      currentTargetRef.current = null;
    }
  }, [cursorPos, stage]);

  // Navigation handlers
  const handleBack = () => {
    if (stage === "calibration") {
      setStage("avatar");
      setCurrentCalibrationIndex(0);
      setCalibrationData([]);
      calibrationDataRef.current = [];
      if (calibrationTimerRef.current) {
        clearInterval(calibrationTimerRef.current);
      }
      setIsCollectingCalibration(false);
    } else if (stage === "test") {
      setStage("calibration");
      setCurrentCalibrationIndex(0);
      setCalibrationData([]);
      calibrationDataRef.current = [];
      calibrationCoefficientsRef.current = null;
    } else if (stage === "cursor") {
      setStage("test");
      setTestCompleted(false);
      setDwellProgress(0);
    } else {
      stopTracking();
      navigate("/control");
    }
  };

  const handleContinue = () => {
    if (stage === "avatar") {
      // Reset calibration data when starting calibration
      calibrationDataRef.current = [];
      setCalibrationData([]);
      setCurrentCalibrationIndex(0);
      setStage("calibration");
    } else if (stage === "test" && testCompleted) {
      setStage("cursor");
    } else if (stage === "cursor") {
      // Save control method to localStorage and database
      const settings = getUserSettings();
      saveUserSettings({ ...settings, controlMethod: "gaze_tracking" });
      sessionStorage.setItem("controlMethod", "gaze_tracking");
      window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
        detail: { method: "gaze_tracking" } 
      }));
      stopTracking();
      navigate("/");
    }
  };

  // Skip calibration and use existing data
  const handleSkipCalibration = () => {
    // Coefficients are already loaded in useEffect
    // Save control method to localStorage and database
    const settings = getUserSettings();
    saveUserSettings({ ...settings, controlMethod: "gaze_tracking" });
    sessionStorage.setItem("controlMethod", "gaze_tracking");
    window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
      detail: { method: "gaze_tracking" } 
    }));
    stopTracking();
    navigate("/");
  };

  // Draw face mesh with iris highlight
  const drawFaceMesh = useCallback((canvas: HTMLCanvasElement, landmarks: FaceLandmark[], color: string) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.fillStyle = color;
    
    const dotIndices = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 
      397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 
      172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
      70, 63, 105, 66, 107, 55, 65, 52, 53, 46,
      300, 293, 334, 296, 336, 285, 295, 282, 283, 276,
      33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246,
      362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398,
      168, 6, 197, 195, 5, 4, 1, 19, 94, 2, 98, 327,
      61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95,
    ];
    
    dotIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw iris points in cyan for visibility
    ctx.fillStyle = "#00ffff";
    [468, 469, 470, 471, 472, 473, 474, 475, 476, 477].forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  }, []);

  // Update face mesh canvas
  useEffect(() => {
    if (stage === "avatar" && meshCanvasRef.current && faceLandmarks) {
      const color = faceDistance === "good" ? "#22c55e" : "#f97316";
      drawFaceMesh(meshCanvasRef.current, faceLandmarks, color);
    }
  }, [faceLandmarks, faceDistance, stage, drawFaceMesh]);

  const getDistanceMessage = () => {
    switch (faceDistance) {
      case "too_close": return t("tooClose");
      case "too_far": return t("tooFar");
      default: return t("distanceGood");
    }
  };

  const renderAvatarStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <h2 className="text-2xl font-bold text-foreground">{t("faceAndEyeDetection")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("faceAndEyeDetectionDesc")}
      </p>
      
      <div className="relative w-80 h-60 bg-black rounded-2xl overflow-hidden border-4 border-border">
        <canvas 
          ref={meshCanvasRef}
          width={320}
          height={240}
          className="w-full h-full"
        />
        {!faceLandmarks && isTracking && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-muted-foreground">{t("searchingForFace")}</p>
          </div>
        )}
      </div>
      
      <div className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
        initError 
          ? "bg-red-500/20 text-red-500"
          : faceDistance === "good" 
            ? "bg-green-500/20 text-green-500" 
            : "bg-orange-500/20 text-orange-500"
      )}>
        {initError ? initError : (isTracking ? getDistanceMessage() : t("initializingTracking"))}
      </div>
      
      <p className="text-muted-foreground text-sm text-center">
        <Eye className="w-4 h-4 inline-block ml-1" />
        {t("eyePointsMarkedCyan")}
      </p>
      
      {/* Skip calibration option if existing calibration exists */}
      {hasExistingCalibration && faceDistance === "good" && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
          <p className="text-sm text-green-600 dark:text-green-400 mb-3 text-center">
            {t("foundPreviousCalibration")}
          </p>
          <div className="flex gap-3 justify-center">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleSkipCalibration}
              className="px-6 py-2 rounded-xl bg-green-500 text-white font-medium"
            >
              {t("usePreviousCalibration")}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setHasExistingCalibration(false)}
              className="px-6 py-2 rounded-xl border border-border bg-card text-foreground font-medium"
            >
              {t("recalibrate")}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );

  // Track previous point for animation
  const [previousPoint, setPreviousPoint] = useState<{ x: number; y: number } | null>(null);
  const [showFallingStar, setShowFallingStar] = useState(false);
  
  // Update previous point when calibration index changes
  useEffect(() => {
    if (currentCalibrationIndex > 0) {
      const prevPointIndex = CALIBRATION_ORDER[currentCalibrationIndex - 1];
      const prevPoint = CALIBRATION_POINTS[prevPointIndex];
      setPreviousPoint({ x: prevPoint.x, y: prevPoint.y });
      setShowFallingStar(true);
      
      // Hide falling star after animation completes
      const timer = setTimeout(() => setShowFallingStar(false), 600);
      return () => clearTimeout(timer);
    }
  }, [currentCalibrationIndex]);

  const renderCalibrationStage = () => {
    const currentPointIndex = CALIBRATION_ORDER[currentCalibrationIndex];
    const currentPoint = CALIBRATION_POINTS[currentPointIndex];
    
    return (
      <>
        {/* Completely clean fullscreen overlay - solid dark background, hide scrollbars */}
        <div className="fixed inset-0 bg-[hsl(var(--background))] z-[9998] overflow-hidden" style={{ backgroundColor: 'hsl(197 69% 12%)' }} />
        
        {/* Calibration content - completely clean */}
        <div className="fixed inset-0 z-[9999]">
          {/* Minimal progress indicator at top */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <span className="text-sm text-muted-foreground/70">
              {currentCalibrationIndex + 1}/{CALIBRATION_ORDER.length}
            </span>
          </div>
          
          {/* Falling star animation between points */}
          <AnimatePresence>
            {showFallingStar && previousPoint && (
              <motion.div
                initial={{ 
                  left: `${(previousPoint.x / 100) * window.innerWidth}px`, 
                  top: `${(previousPoint.y / 100) * window.innerHeight}px`,
                  opacity: 1,
                  scale: 1
                }}
                animate={{ 
                  left: `${(currentPoint.x / 100) * window.innerWidth}px`, 
                  top: `${(currentPoint.y / 100) * window.innerHeight}px`,
                  opacity: 0,
                  scale: 0.3
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="fixed z-[10001] pointer-events-none"
                style={{ transform: 'translate(-50%, -50%)' }}
              >
                {/* Star with glowing tail */}
                <div className="relative">
                  <div 
                    className="w-6 h-6 rounded-full bg-accent"
                    style={{
                      boxShadow: '0 0 20px hsl(var(--accent)), 0 0 40px hsl(var(--accent) / 0.7), 0 0 60px hsl(var(--accent) / 0.4)'
                    }}
                  />
                  {/* Tail effect */}
                  <motion.div
                    className="absolute inset-0 rounded-full bg-accent/50"
                    animate={{ scale: [1, 2, 3], opacity: [0.5, 0.2, 0] }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Simple calibration dot - orange, grows/shrinks during calibration */}
          <motion.div
            key={currentCalibrationIndex}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: showFallingStar ? 0.4 : 0 }}
            className="fixed z-[10000]"
            style={{
              left: `${(currentPoint.x / 100) * window.innerWidth}px`,
              top: `${(currentPoint.y / 100) * window.innerHeight}px`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <motion.div
              animate={{
                scale: isCollectingCalibration ? [1, 1.4, 1] : 1
              }}
              transition={{
                duration: 0.6,
                repeat: isCollectingCalibration ? Infinity : 0,
                ease: "easeInOut"
              }}
              className="w-12 h-12 rounded-full bg-accent flex items-center justify-center"
              style={{
                boxShadow: '0 0 40px hsl(var(--accent)), 0 0 80px hsl(var(--accent) / 0.5)'
              }}
            >
              <div className="w-4 h-4 rounded-full bg-white" />
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  };

  const renderTestStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6 relative">
      <h2 className="text-2xl font-bold text-foreground">{t("testGazeTracking")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("lookAtContinueButton")}
      </p>
      
      <motion.button
        id="test-target"
        className={cn(
          "relative px-12 py-6 rounded-2xl text-xl font-bold transition-all",
          testCompleted 
            ? "bg-accent text-accent-foreground" 
            : "bg-primary text-primary-foreground"
        )}
        whileHover={{ scale: 1.05 }}
        onClick={handleContinue}
        disabled={!testCompleted}
      >
        <Target className="w-6 h-6 inline-block ml-2" />
        {testCompleted ? t("excellentClickToContinue") : t("continue")}
        
        {dwellProgress > 0 && !testCompleted && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <rect
              x="2"
              y="2"
              width="calc(100% - 4px)"
              height="calc(100% - 4px)"
              rx="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeDasharray={`${dwellProgress * 4} 400`}
              className="text-accent"
            />
          </svg>
        )}
      </motion.button>
      
      {/* Virtual cursor with dwell ring */}
      <div
        ref={cursorElementRef}
        className="fixed pointer-events-none z-[999999]"
        style={{
          left: cursorRef.current.x,
          top: cursorRef.current.y,
          transform: "translate(-50%, -50%)"
        }}
      >
        <div className={`relative w-14 h-14 flex items-center justify-center ${dwellProgress > 0 ? 'scale-110' : ''} transition-transform duration-150`}>
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth="6"
              opacity="0.4"
            />
            <circle
              cx="28"
              cy="28"
              r="22"
              fill="none"
              stroke="hsl(var(--accent))"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${dwellProgress * 1.38} 138`}
              className="transition-all duration-75"
              style={{ 
                filter: dwellProgress > 0 ? 'drop-shadow(0 0 8px hsl(var(--accent)))' : 'none' 
              }}
            />
          </svg>
          <div 
            className="w-4 h-4 rounded-full bg-white border-2 border-accent shadow-lg transition-all duration-150"
            style={{
              boxShadow: dwellProgress > 0 
                ? '0 0 12px hsl(var(--accent)), 0 2px 4px rgba(0,0,0,0.3)' 
                : '0 2px 4px rgba(0,0,0,0.3)'
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderCursorStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <h2 className="text-2xl font-bold text-foreground">{t("gazeActiveTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("gazeActiveDesc")}
      </p>
      
      <div className="grid grid-cols-2 gap-4 mt-8">
        {[`${t("testButton")} 1`, `${t("testButton")} 2`, `${t("testButton")} 3`, `${t("testButton")} 4`].map((label, index) => (
          <DwellButton 
            key={index} 
            label={label} 
            cursorPos={cursorPos}
          />
        ))}
      </div>
      
      {/* Virtual cursor with dwell ring */}
      <div
        ref={cursorElementRef}
        className="fixed pointer-events-none z-[999999]"
        style={{
          left: cursorRef.current.x,
          top: cursorRef.current.y,
          transform: "translate(-50%, -50%)"
        }}
      >
        <DwellCursor cursorPos={cursorPos} />
      </div>
    </div>
  );

  const stages: SetupStage[] = ["avatar", "calibration", "test", "cursor"];
  const stageIndex = stages.indexOf(stage);

  return (
    <MainLayout>
      <video ref={videoRef} className="hidden" playsInline muted />
      
      <div className="flex-1 flex flex-col p-4 bg-background overflow-auto" dir={dir}>
        {/* Stage indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {stages.map((s, i) => (
            <React.Fragment key={s}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
                stage === s 
                  ? "bg-primary text-primary-foreground" 
                  : (stageIndex > i 
                      ? "bg-accent text-accent-foreground" 
                      : "bg-muted text-muted-foreground")
              )}>
                {stageIndex > i ? (
                  <Check className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              {i < stages.length - 1 && (
                <div className={cn(
                  "w-8 h-1 rounded",
                  stageIndex > i 
                    ? "bg-accent" 
                    : "bg-muted"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 flex flex-col"
          >
            {stage === "avatar" && renderAvatarStage()}
            {stage === "calibration" && renderCalibrationStage()}
            {stage === "test" && renderTestStage()}
            {stage === "cursor" && renderCursorStage()}
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation buttons */}
        <div className="flex gap-3 justify-center mt-6">
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
              "bg-card hover:bg-muted border-2 border-border",
              "text-muted-foreground hover:text-foreground"
            )}
          >
            <ChevronRight className="w-5 h-5" />
            <span>{t("back")}</span>
          </button>

          {(stage === "avatar" || (stage === "test" && testCompleted) || stage === "cursor") && (
            <button
              onClick={handleContinue}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-xl transition-all text-base font-medium",
                "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
              )}
            >
              <Check className="w-5 h-5" />
              <span>{stage === "cursor" ? t("finish") : t("continue")}</span>
            </button>
          )}
        </div>
        
        {/* Floating Scroll Buttons - separated for maximum reach */}
        {(stage === "test" || stage === "cursor") && (
          <>
            <button
              onMouseEnter={() => startScrolling('up')}
              onMouseLeave={stopScrolling}
              onClick={() => window.scrollBy({ top: -200, behavior: 'smooth' })}
              className={cn(
                "fixed left-4 top-4 w-16 h-16 rounded-2xl flex items-center justify-center z-50",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronUp className="w-8 h-8 text-primary" />
            </button>
            <button
              onMouseEnter={() => startScrolling('down')}
              onMouseLeave={stopScrolling}
              onClick={() => window.scrollBy({ top: 200, behavior: 'smooth' })}
              className={cn(
                "fixed left-4 bottom-20 w-16 h-16 rounded-2xl flex items-center justify-center z-50",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronDown className="w-8 h-8 text-primary" />
            </button>
          </>
        )}
      </div>
    </MainLayout>
  );
}

// Dwell button component for cursor stage
function DwellButton({ 
  label, 
  cursorPos 
}: { 
  label: string; 
  cursorPos: { x: number; y: number };
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const isOver = 
      cursorPos.x >= rect.left &&
      cursorPos.x <= rect.right &&
      cursorPos.y >= rect.top &&
      cursorPos.y <= rect.bottom;
    
    setIsHovered(isOver);
    
    if (isOver) {
      if (!dwellTimerRef.current) {
        dwellTimerRef.current = setInterval(() => {
          setDwellProgress(prev => {
            if (prev >= 100) {
              clearInterval(dwellTimerRef.current!);
              dwellTimerRef.current = undefined;
              buttonRef.current?.click();
              return 0;
            }
            return prev + 5;
          });
        }, 50);
      }
    } else {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
        dwellTimerRef.current = undefined;
      }
      setDwellProgress(0);
    }
    
    return () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
    };
  }, [cursorPos]);
  
  return (
    <button
      ref={buttonRef}
      className={cn(
        "relative px-8 py-6 rounded-xl text-lg font-medium transition-all",
        "bg-card border-2 hover:border-primary",
        isHovered ? "border-primary bg-primary/10" : "border-border"
      )}
    >
      {label}
      
      {dwellProgress > 0 && (
        <div 
          className="absolute bottom-0 left-0 h-1 bg-accent rounded-b-xl transition-all"
          style={{ width: `${dwellProgress}%` }}
        />
      )}
    </button>
  );
}

// Dwell cursor component with circular progress
function DwellCursor({ cursorPos }: { cursorPos: { x: number; y: number } }) {
  const [dwellProgress, setDwellProgress] = useState(0);
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  const lastElementRef = useRef<Element | null>(null);
  const isActiveRef = useRef(false);
  
  useEffect(() => {
    const elementsAtPoint = document.elementsFromPoint(cursorPos.x, cursorPos.y);
    const interactive = elementsAtPoint.find(el => 
      el.tagName === 'BUTTON' || 
      el.tagName === 'A' || 
      el.getAttribute('role') === 'button' ||
      el.classList.contains('dwell-target')
    );
    
    if (interactive) {
      if (interactive === lastElementRef.current && isActiveRef.current) {
        return;
      }
      
      lastElementRef.current = interactive;
      isActiveRef.current = true;
      setDwellProgress(0);
      
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
      
      dwellTimerRef.current = setInterval(() => {
        setDwellProgress(prev => {
          if (prev >= 100) {
            clearInterval(dwellTimerRef.current!);
            dwellTimerRef.current = undefined;
            isActiveRef.current = false;
            (interactive as HTMLElement).click();
            lastElementRef.current = null;
            return 0;
          }
          return prev + 5;
        });
      }, 50);
    } else {
      if (lastElementRef.current || isActiveRef.current) {
        lastElementRef.current = null;
        isActiveRef.current = false;
        setDwellProgress(0);
        if (dwellTimerRef.current) {
          clearInterval(dwellTimerRef.current);
          dwellTimerRef.current = undefined;
        }
      }
    }
  }, [cursorPos]);
  
  useEffect(() => {
    return () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
    };
  }, []);
  
  return (
    <div className={`relative w-14 h-14 flex items-center justify-center ${dwellProgress > 0 ? 'scale-110' : ''} transition-transform duration-150`}>
      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 56 56">
        <circle
          cx="28"
          cy="28"
          r="22"
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="6"
          opacity="0.4"
        />
        <circle
          cx="28"
          cy="28"
          r="22"
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${dwellProgress * 1.38} 138`}
          className="transition-all duration-75"
          style={{ 
            filter: dwellProgress > 0 ? 'drop-shadow(0 0 8px hsl(var(--accent)))' : 'none' 
          }}
        />
      </svg>
      <div 
        className="w-4 h-4 rounded-full bg-white border-2 border-accent shadow-lg transition-all duration-150"
        style={{
          boxShadow: dwellProgress > 0 
            ? '0 0 12px hsl(var(--accent)), 0 2px 4px rgba(0,0,0,0.3)' 
            : '0 2px 4px rgba(0,0,0,0.3)'
        }}
      />
    </div>
  );
}
